import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Trophy, CheckCircle, XCircle, FileText, TrendingUp, Award, Home, Eye,
  Download, Target, Clock, BarChart3, PieChart, Users, Star
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const TestResultEnhanced = () => {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(location.state?.result || null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!result) {
      navigate('/student/tests');
    } else {
      calculateAnalytics();
    }
  }, []);

  const calculateAnalytics = () => {
    if (!result) return;

    // Calculate subject-wise performance (mock data - you'll replace with real backend data)
    const subjectWiseData = [
      { subject: 'Quantitative Aptitude', correct: Math.floor(result.correct_answers * 0.3), total: Math.floor(result.total_questions * 0.3) },
      { subject: 'Reasoning', correct: Math.floor(result.correct_answers * 0.25), total: Math.floor(result.total_questions * 0.25) },
      { subject: 'English', correct: Math.floor(result.correct_answers * 0.25), total: Math.floor(result.total_questions * 0.25) },
      { subject: 'General Awareness', correct: Math.floor(result.correct_answers * 0.2), total: Math.floor(result.total_questions * 0.2) }
    ];

    const subjectWise = subjectWiseData.map(s => ({
      ...s,
      percentage: ((s.correct / s.total) * 100).toFixed(1),
      accuracy: ((s.correct / s.total) * 100).toFixed(0)
    }));

    // Calculate percentile (mock - replace with backend calculation)
    const percentile = Math.min(99, Math.floor((result.score / result.total_marks) * 85 + Math.random() * 10));
    
    // Rank calculation (mock)
    const rank = Math.floor(Math.random() * 500) + 1;
    const totalAttempted = 5000 + Math.floor(Math.random() * 1000);

    setAnalytics({
      subjectWise,
      percentile,
      rank,
      totalAttempted,
      accuracy: ((result.correct_answers / result.total_questions) * 100).toFixed(1),
      timeTaken: result.time_taken || '45 min 30 sec',
      avgTimePerQuestion: '1.5 min'
    });
  };

  const downloadScorecard = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(220, 38, 38);
    doc.text('Vidya365 - Test Scorecard', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Test: ${result.test_name || 'Mock Test'}`, 20, 35);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 42);
    
    // Score Box
    doc.setFillColor(239, 246, 255);
    doc.rect(20, 50, 170, 30, 'F');
    doc.setFontSize(16);
    doc.text(`Score: ${result.score}/${result.total_marks} (${percentage}%)`, 105, 65, { align: 'center' });
    doc.text(`Percentile: ${analytics?.percentile || 'N/A'}`, 105, 73, { align: 'center' });
    
    // Performance Table
    doc.autoTable({
      startY: 90,
      head: [['Metric', 'Value']],
      body: [
        ['Correct Answers', result.correct_answers.toString()],
        ['Wrong Answers', result.wrong_answers.toString()],
        ['Unattempted', result.unattempted.toString()],
        ['Accuracy', `${analytics?.accuracy || '0'}%`],
        ['Rank', `${analytics?.rank || 'N/A'} / ${analytics?.totalAttempted || 'N/A'}`],
      ],
    });
    
    // Subject-wise Performance
    if (analytics?.subjectWise) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 10,
        head: [['Subject', 'Correct', 'Total', 'Percentage']],
        body: analytics.subjectWise.map(s => [
          s.subject,
          s.correct.toString(),
          s.total.toString(),
          `${s.percentage}%`
        ]),
      });
    }
    
    doc.save(`Vidya365-Scorecard-${Date.now()}.pdf`);
  };

  if (!result) {
    return null;
  }

  const percentage = ((result.score / result.total_marks) * 100).toFixed(2);
  const isPassed = result.score >= result.passing_marks;

  const COLORS = ['#22c55e', '#ef4444', '#6b7280'];
  const pieData = [
    { name: 'Correct', value: result.correct_answers },
    { name: 'Wrong', value: result.wrong_answers },
    { name: 'Unattempted', value: result.unattempted }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      <Header />

      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Result Header - Enhanced */}
            <Card className={`border-t-8 ${isPassed ? 'border-t-green-500' : 'border-t-red-500'} mb-6 relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full filter blur-3xl opacity-50"></div>
              <CardContent className="p-8 relative z-10">
                <div className="grid md:grid-cols-3 gap-6 items-center">
                  <div className="text-center">
                    <div className={`w-32 h-32 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      isPassed ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {isPassed ? (
                        <Trophy className="text-green-600" size={64} />
                      ) : (
                        <Target className="text-red-600" size={64} />
                      )}
                    </div>
                    <h1 className={`text-3xl font-bold ${isPassed ? 'text-green-700' : 'text-red-700'}`}>
                      {isPassed ? '🎉 Congratulations!' : '💪 Keep Going!'}
                    </h1>
                  </div>

                  <div className="text-center">
                    <div className="bg-white rounded-xl shadow-xl p-6 border-4 border-red-600">
                      <p className="text-gray-600 mb-2 text-sm">Your Score</p>
                      <p className="text-6xl font-bold text-red-600 mb-1">
                        {result.score}
                      </p>
                      <p className="text-xl text-gray-400">out of {result.total_marks}</p>
                      <p className="text-3xl font-bold text-gray-700 mt-2">{percentage}%</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-purple-500">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">Percentile</span>
                        <span className="text-2xl font-bold text-purple-600">{analytics?.percentile || '--'}th</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-blue-500">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">Rank</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {analytics?.rank || '--'}/{analytics?.totalAttempted || '--'}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-orange-500">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">Accuracy</span>
                        <span className="text-2xl font-bold text-orange-600">{analytics?.accuracy || '0'}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-center gap-3">
                  <Button onClick={downloadScorecard} className="bg-green-600 hover:bg-green-700">
                    <Download className="mr-2" size={18} />
                    Download Scorecard
                  </Button>
                  <Link to={`/student/test/${testId}/review`} state={{ result }}>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Eye className="mr-2" size={18} />
                      View Solutions
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <div className="mb-6 border-b">
              <div className="flex space-x-4">
                {['overview', 'analysis', 'comparison'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 px-4 font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'border-b-2 border-red-600 text-red-600'
                        : 'text-gray-600 hover:text-red-600'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="grid md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-6 text-center">
                      <CheckCircle className="mx-auto text-green-600 mb-3" size={40} />
                      <p className="text-sm text-gray-600 mb-1">Correct</p>
                      <p className="text-4xl font-bold text-green-600">{result.correct_answers}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6 text-center">
                      <XCircle className="mx-auto text-red-600 mb-3" size={40} />
                      <p className="text-sm text-gray-600 mb-1">Wrong</p>
                      <p className="text-4xl font-bold text-red-600">{result.wrong_answers}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6 text-center">
                      <FileText className="mx-auto text-gray-600 mb-3" size={40} />
                      <p className="text-sm text-gray-600 mb-1">Skipped</p>
                      <p className="text-4xl font-bold text-gray-600">{result.unattempted}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6 text-center">
                      <Target className="mx-auto text-purple-600 mb-3" size={40} />
                      <p className="text-sm text-gray-600 mb-1">Accuracy</p>
                      <p className="text-4xl font-bold text-purple-600">{analytics?.accuracy}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <PieChart className="mr-2" size={20} />
                        Answer Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Subject-wise Performance */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <BarChart3 className="mr-2" size={20} />
                        Subject-wise Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics?.subjectWise || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="subject" angle={-15} textAnchor="end" height={80} fontSize={12} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="correct" fill="#22c55e" name="Correct" />
                          <Bar dataKey="total" fill="#e5e7eb" name="Total" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Analysis Tab */}
            {activeTab === 'analysis' && analytics && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Detailed Subject Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics.subjectWise.map((subject, idx) => (
                        <div key={idx} className="border-b pb-4 last:border-0">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold text-gray-800">{subject.subject}</span>
                            <span className="text-sm text-gray-600">
                              {subject.correct}/{subject.total} ({subject.percentage}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full ${
                                subject.percentage >= 75 
                                  ? 'bg-green-500' 
                                  : subject.percentage >= 50 
                                  ? 'bg-yellow-500' 
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${subject.percentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {subject.percentage >= 75 
                              ? '✅ Excellent! Keep it up!' 
                              : subject.percentage >= 50 
                              ? '⚠️ Good, but room for improvement' 
                              : '⚠️ Needs more practice'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Strengths & Weaknesses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-green-700 mb-3 flex items-center">
                          <Star className="mr-2" size={18} />
                          Strengths
                        </h3>
                        <ul className="space-y-2">
                          {analytics.subjectWise
                            .filter(s => parseFloat(s.percentage) >= 75)
                            .map((s, idx) => (
                              <li key={idx} className="flex items-center text-sm">
                                <CheckCircle className="mr-2 text-green-600" size={16} />
                                {s.subject} ({s.percentage}%)
                              </li>
                            ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-red-700 mb-3 flex items-center">
                          <Target className="mr-2" size={18} />
                          Needs Improvement
                        </h3>
                        <ul className="space-y-2">
                          {analytics.subjectWise
                            .filter(s => parseFloat(s.percentage) < 75)
                            .map((s, idx) => (
                              <li key={idx} className="flex items-center text-sm">
                                <XCircle className="mr-2 text-red-600" size={16} />
                                {s.subject} ({s.percentage}%)
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Comparison Tab */}
            {activeTab === 'comparison' && analytics && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="mr-2" size={20} />
                    Compare with Others
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold mb-4">Your Performance Ranking</h3>
                      <div className="flex items-center justify-around">
                        <div className="text-center">
                          <p className="text-4xl font-bold text-purple-600">{analytics.percentile}th</p>
                          <p className="text-sm text-gray-600">Percentile</p>
                        </div>
                        <div className="text-center">
                          <p className="text-4xl font-bold text-blue-600">{analytics.rank}</p>
                          <p className="text-sm text-gray-600">Your Rank</p>
                        </div>
                        <div className="text-center">
                          <p className="text-4xl font-bold text-gray-600">{analytics.totalAttempted}</p>
                          <p className="text-sm text-gray-600">Total Students</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <div className="flex items-center mb-2">
                          <Trophy className="text-yellow-600 mr-2" size={24} />
                          <span className="font-semibold">Top Score</span>
                        </div>
                        <p className="text-3xl font-bold text-yellow-700">{result.total_marks}</p>
                        <p className="text-xs text-gray-600">100%</p>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center mb-2">
                          <TrendingUp className="text-blue-600 mr-2" size={24} />
                          <span className="font-semibold">Average Score</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-700">
                          {Math.floor(result.total_marks * 0.6)}
                        </p>
                        <p className="text-xs text-gray-600">~60%</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center mb-2">
                          <Award className="text-green-600 mr-2" size={24} />
                          <span className="font-semibold">Your Score</span>
                        </div>
                        <p className="text-3xl font-bold text-green-700">{result.score}</p>
                        <p className="text-xs text-gray-600">{percentage}%</p>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>Performance Insight:</strong> You scored better than <strong className="text-purple-600">{analytics.percentile}%</strong> of students who took this test. 
                        {parseFloat(percentage) >= 75 
                          ? " Excellent work! You're in the top performers." 
                          : parseFloat(percentage) >= 50 
                          ? " Good job! With more practice, you can reach the top tier." 
                          : " Keep practicing! Focus on your weak areas to improve your rank."}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <Link to={`/student/test/${testId}/review`} state={{ result }}>
                <Button className="w-full bg-red-600 hover:bg-red-700" size="lg">
                  <Eye className="mr-2" size={18} />
                  View Detailed Solutions
                </Button>
              </Link>
              
              <Link to="/student/tests">
                <Button variant="outline" className="w-full border-2" size="lg">
                  <FileText className="mr-2" size={18} />
                  Take More Tests
                </Button>
              </Link>
              
              <Link to="/student/dashboard">
                <Button variant="outline" className="w-full border-2" size="lg">
                  <Home className="mr-2" size={18} />
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestResultEnhanced;
